import {
  BriefcaseBusiness,
  CalendarClock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";

import api from "../api/client";
import { ErrorNotice, LoadingState, SuccessNotice } from "./Feedback";
import { useAuth } from "../context/AuthContext";
import { useWorkspace } from "../context/WorkspaceContext";
import type {
  EmploymentContract,
  EmploymentRelationship,
  OrganizationMember,
  Paginated,
} from "../types/api";
import { unwrapResults } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatDate } from "../utils/format";
import DateField from "./DateField";

function today() {
  const value = new Date();
  value.setMinutes(value.getMinutes() - value.getTimezoneOffset());
  return value.toISOString().slice(0, 10);
}

function oneYearLater(value: string) {
  const result = new Date(`${value}T12:00:00`);
  result.setFullYear(result.getFullYear() + 1);
  result.setDate(result.getDate() - 1);
  return result.toISOString().slice(0, 10);
}

type EmploymentSettingsProps = {
  scopedEmploymentId?: number | null;
  onSaved?: (employmentId: number | null) => void;
};

export default function EmploymentSettings({
  scopedEmploymentId,
  onSaved,
}: EmploymentSettingsProps) {
  const { user } = useAuth();
  const { organizationId, organization, capabilities } = useWorkspace();
  const [targetUser, setTargetUser] = useState<number | null>(user?.id ?? null);
  const [members, setMembers] = useState<OrganizationMember[]>([]);
  const [employments, setEmployments] = useState<EmploymentRelationship[]>([]);
  const [editingContract, setEditingContract] = useState<EmploymentContract | null>(
    null,
  );
  const [showContractForm, setShowContractForm] = useState(false);
  const [employmentChoice, setEmploymentChoice] = useState("new");
  const [employerName, setEmployerName] = useState("");
  const [contractHours, setContractHours] = useState("40");
  const [contractFulltime, setContractFulltime] = useState("");
  const [contractType, setContractType] = useState<"permanent" | "fixed_term">(
    "permanent",
  );
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(oneYearLater(today()));
  const [label, setLabel] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingContractId, setDeletingContractId] = useState<number | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canEdit =
    !organizationId ||
    (capabilities.canManageOrganizationPolicies && !capabilities.organizationReadOnly);

  const query = useMemo(() => {
    const params = new URLSearchParams();
    if (organizationId) params.set("organization", String(organizationId));
    if (targetUser) params.set("user", String(targetUser));
    return params.toString();
  }, [organizationId, targetUser]);

  useEffect(() => {
    setTargetUser(user?.id ?? null);
    setMembers([]);
    setEmploymentChoice("new");
    setEditingContract(null);
  }, [organizationId, user?.id]);

  useEffect(() => {
    if (scopedEmploymentId === null) setEmploymentChoice("new");
  }, [scopedEmploymentId]);

  useEffect(() => {
    if (!organizationId || !capabilities.canManageOrganizationPolicies) return;
    void api
      .get<OrganizationMember[]>(`/organizations/${organizationId}/members/`)
      .then((response) =>
        setMembers(response.data.filter((member) => member.status === "active")),
      )
      .catch(() => setError("De medewerkers konden niet worden geladen."));
  }, [capabilities.canManageOrganizationPolicies, organizationId]);

  const load = useCallback(async () => {
    if (!targetUser) return;
    setLoading(true);
    setError("");
    try {
      const suffix = query ? `?${query}` : "";
      const response = await api.get<
        Paginated<EmploymentRelationship> | EmploymentRelationship[]
      >(`/organizations/employments/${suffix}`);
      const rows = unwrapResults(response.data);
      setEmployments(rows);
      setEmploymentChoice((current) => {
        if (scopedEmploymentId != null) {
          return String(scopedEmploymentId);
        }
        if (current !== "new" && rows.some((row) => String(row.id) === current)) {
          return current;
        }
        return organizationId && rows[0] ? String(rows[0].id) : current;
      });
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(
          requestError,
          "De contractgegevens konden niet worden geladen.",
        ),
      );
    } finally {
      setLoading(false);
    }
  }, [organizationId, query, scopedEmploymentId, targetUser]);

  useEffect(() => {
    void load();
  }, [load]);

  const createContract = async (event: FormEvent) => {
    event.preventDefault();
    if (!targetUser) return;
    setSaving(true);
    setError("");
    try {
      const suffix = query ? `?${query}` : "";
      let employmentId = editingContract
        ? editingContract.employment
        : scopedEmploymentId != null
        ? scopedEmploymentId
        : employmentChoice === "new"
        ? null
        : Number(employmentChoice);
      if (!editingContract && !employmentId) {
        const employmentResponse = await api.post<EmploymentRelationship>(
          `/organizations/employments/${suffix}`,
          {
            organization: organizationId,
            user: targetUser,
            employer_name: organizationId ? "" : employerName.trim(),
            fulltime_hours_per_week: contractFulltime || "40",
          },
        );
        employmentId = employmentResponse.data.id;
      }
      const payload = {
        organization: organizationId,
        user: targetUser,
        employment: employmentId,
        contract_type: contractType,
        start_date: startDate,
        end_date:
          contractType === "fixed_term" || editingContract ? endDate || null : null,
        label: label.trim(),
        contract_hours_per_week: contractHours,
        fulltime_hours_per_week: organizationId
          ? contractFulltime || null
          : contractFulltime || "40",
      };
      if (editingContract) {
        await api.patch(
          `/organizations/employment-contracts/${editingContract.id}/${suffix}`,
          payload,
        );
      } else {
        await api.post(`/organizations/employment-contracts/${suffix}`, payload);
      }
      setSuccess(
        editingContract
          ? "Het contract is bijgewerkt."
          : employmentChoice === "new"
          ? "Werkgever en contract zijn toegevoegd."
          : "Het opvolgende contract is toegevoegd.",
      );
      setEditingContract(null);
      setShowContractForm(false);
      setEmployerName("");
      setLabel("");
      await load();
      if (!organizationId) setEmploymentChoice(String(employmentId));
      onSaved?.(employmentId);
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het contract kon niet worden opgeslagen."),
      );
    } finally {
      setSaving(false);
    }
  };

  const editContract = (
    employment: EmploymentRelationship,
    contract: EmploymentContract,
  ) => {
    setEditingContract(contract);
    setEmploymentChoice(String(employment.id));
    setContractType(contract.contract_type);
    setStartDate(contract.start_date);
    setEndDate(contract.end_date ?? "");
    setLabel(contract.label);
    setContractHours(contract.contract_hours_per_week);
    setContractFulltime(contract.fulltime_hours_per_week ?? "");
    setShowContractForm(true);
    setError("");
    setSuccess("");
  };

  const cancelEdit = () => {
    setEditingContract(null);
    setShowContractForm(false);
    setContractType("permanent");
    setStartDate(today());
    setEndDate(oneYearLater(today()));
    setLabel("");
    setContractHours("40");
    setContractFulltime("");
  };

  const removeContract = async (contract: EmploymentContract) => {
    if (!window.confirm("Dit contract verwijderen?")) return;
    setDeletingContractId(contract.id);
    setError("");
    try {
      const suffix = query ? `?${query}` : "";
      await api.delete(`/organizations/employment-contracts/${contract.id}/${suffix}`);
      if (editingContract?.id === contract.id) cancelEdit();
      setSuccess("Het contract is verwijderd.");
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het contract kon niet worden verwijderd."),
      );
    } finally {
      setDeletingContractId(null);
    }
  };

  if (loading) return <LoadingState label="Contracten laden..." />;

  // scopedEmploymentId === null betekent: alleen het formulier voor een nieuwe
  // werkgever. De contracthistorie van bestaande werkgevers hoort daar niet.
  const isNewEmployer = scopedEmploymentId === null;
  const visibleEmployments = isNewEmployer
    ? []
    : scopedEmploymentId != null
    ? employments.filter((employment) => employment.id === scopedEmploymentId)
    : employments;
  const hasContracts = visibleEmployments.some(
    (employment) => employment.contracts.length > 0,
  );

  const openNewContract = () => {
    setEditingContract(null);
    setContractType("permanent");
    setStartDate(today());
    setEndDate(oneYearLater(today()));
    setLabel("");
    setContractHours("40");
    setContractFulltime("");
    setShowContractForm(true);
  };

  return (
    <div className="settings-stack">
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      {members.length > 0 && (
        <section className="surface settings-section settings-context-bar">
          <label>
            <span>Medewerker</span>
            <select
              value={targetUser ?? ""}
              onChange={(event) => {
                setTargetUser(Number(event.target.value));
                setEmploymentChoice("new");
                setEditingContract(null);
              }}
            >
              {members.map((member) => (
                <option key={member.user} value={member.user}>
                  {member.display_name}
                </option>
              ))}
            </select>
          </label>
        </section>
      )}

      {!isNewEmployer && (
        <section className="surface settings-section">
          <div className="settings-heading settings-heading-with-action">
            <div>
              <h2>Contracthistorie</h2>
              <p>
                Opvolgende contracten bij dezelfde werkgever delen dezelfde
                saldorekening.
              </p>
            </div>
            {canEdit && hasContracts && (
              <button className="button subtle" type="button" onClick={openNewContract}>
                <Plus size={17} /> Contract toevoegen
              </button>
            )}
          </div>
          <div className="employment-list">
            {visibleEmployments.length === 0 && (
              <div className="empty-state compact">
                <BriefcaseBusiness size={24} />
                <h2>Nog geen contract</h2>
                <p>Voeg een werkgever en de eerste contractperiode toe.</p>
              </div>
            )}
            {visibleEmployments.map((employment) => (
              <div className="employment-group" key={employment.id}>
                <div className="employment-group-heading">
                  <span>
                    <strong>{employment.display_name}</strong>
                    <small>{employment.user_name}</small>
                  </span>
                  <span>{employment.contracts.length} contract(en)</span>
                </div>
                <div className="contract-timeline">
                  {employment.contracts.map((contract) => (
                    <article key={contract.id}>
                      <span className="timeline-marker" aria-hidden="true" />
                      <div>
                        <strong>
                          {contract.label ||
                            (contract.contract_type === "permanent"
                              ? "Vast contract"
                              : "Tijdelijk contract")}
                        </strong>
                        <small>
                          {formatDate(contract.start_date)}
                          {contract.end_date
                            ? ` t/m ${formatDate(contract.end_date)}`
                            : " - doorlopend"}
                        </small>
                      </div>
                      <span className="contract-timeline-actions">
                        <span className="status-chip neutral">
                          {contract.contract_type === "permanent"
                            ? "Vast"
                            : "Tijdelijk"}
                        </span>
                        {canEdit && (
                          <>
                            <button
                              className="icon-button"
                              type="button"
                              title="Contract wijzigen"
                              aria-label={`${contract.label || "Contract"} wijzigen`}
                              onClick={() => editContract(employment, contract)}
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              className="icon-button danger"
                              type="button"
                              title="Contract verwijderen"
                              aria-label={`${contract.label || "Contract"} verwijderen`}
                              disabled={deletingContractId === contract.id}
                              onClick={() => void removeContract(contract)}
                            >
                              <Trash2 size={15} />
                            </button>
                          </>
                        )}
                      </span>
                    </article>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {canEdit &&
        (isNewEmployer || !hasContracts || showContractForm || editingContract) && (
          <form className="surface settings-section" onSubmit={createContract}>
            <div className="settings-heading settings-heading-with-action">
              <div>
                <h2>
                  {editingContract
                    ? "Contract wijzigen"
                    : isNewEmployer
                    ? "Werkgever en eerste contract"
                    : "Contract toevoegen"}
                </h2>
                <p>
                  {editingContract
                    ? "Pas de contractvorm, periode of omschrijving aan."
                    : isNewEmployer
                    ? "Een nieuwe werkgever begint met saldo 0."
                    : "Kies dezelfde werkgever voor een verlenging; een nieuwe werkgever begint met saldo 0."}
                </p>
              </div>
              {(editingContract || showContractForm) && hasContracts && (
                <button
                  className="icon-button"
                  type="button"
                  title="Formulier sluiten"
                  onClick={cancelEdit}
                >
                  <X size={17} />
                </button>
              )}
            </div>
            {!organizationId && !editingContract && scopedEmploymentId === undefined && (
              <div className="form-row">
                <label>
                  <span>Werkgeverrelatie</span>
                  <select
                    value={employmentChoice}
                    onChange={(event) => setEmploymentChoice(event.target.value)}
                  >
                    <option value="new">Nieuwe werkgever</option>
                    {employments.map((employment) => (
                      <option key={employment.id} value={employment.id}>
                        Verlenging bij {employment.display_name}
                      </option>
                    ))}
                  </select>
                </label>
                {employmentChoice === "new" && (
                  <label>
                    <span>Naam werkgever</span>
                    <input
                      value={employerName}
                      onChange={(event) => setEmployerName(event.target.value)}
                      required
                    />
                  </label>
                )}
              </div>
            )}
            {!organizationId && !editingContract && scopedEmploymentId === null && (
              <label>
                <span>Naam werkgever</span>
                <input
                  value={employerName}
                  onChange={(event) => setEmployerName(event.target.value)}
                  required
                />
              </label>
            )}
            <div className="form-row">
              <label>
                <span>Contractvorm</span>
                <select
                  value={contractType}
                  onChange={(event) => {
                    const value = event.target.value as "permanent" | "fixed_term";
                    setContractType(value);
                    if (value === "fixed_term" && !endDate) {
                      setEndDate(oneYearLater(startDate));
                    }
                  }}
                >
                  <option value="permanent">Vast contract</option>
                  <option value="fixed_term">Tijdelijk contract</option>
                </select>
              </label>
              <label>
                <span>Omschrijving</span>
                <input
                  value={label}
                  onChange={(event) => setLabel(event.target.value)}
                  placeholder="Bijvoorbeeld jaarcontract 2027"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                <span>Contracturen per week</span>
                <span className="number-suffix">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={contractHours}
                    onChange={(event) => setContractHours(event.target.value)}
                    required
                  />
                  <small>uur</small>
                </span>
              </label>
              <label>
                <span>Fulltime basis (uren per week)</span>
                <span className="number-suffix">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={contractFulltime}
                    placeholder="40"
                    onChange={(event) => setContractFulltime(event.target.value)}
                    required={!organizationId}
                  />
                  <small>uur</small>
                </span>
              </label>
            </div>
            {Number(contractFulltime || "40") > 0 && (
              <div className="contract-impact-note">
                <BriefcaseBusiness size={18} />
                <span>
                  Je werkt{" "}
                  <strong>
                    {Math.round(
                      (Number(contractHours) / Number(contractFulltime || "40")) * 100,
                    )}
                    %
                  </strong>{" "}
                  van fulltime
                  {Number(contractHours) > Number(contractFulltime || "40")
                    ? " — let op: contracturen zijn hoger dan de fulltime uren."
                    : ""}
                  .
                </span>
              </div>
            )}
            <div className="form-row">
              <label>
                <span>Startdatum</span>
                <DateField
                  value={startDate}
                  onChange={(value) => {
                    setStartDate(value);
                    if (contractType === "fixed_term") {
                      setEndDate(oneYearLater(value));
                    }
                  }}
                  required
                />
              </label>
              {(contractType === "fixed_term" || editingContract) && (
                <label>
                  <span>
                    Einddatum{contractType === "permanent" ? " (bij beëindiging)" : ""}
                  </span>
                  <DateField
                    min={startDate}
                    value={endDate}
                    onChange={(value) => setEndDate(value)}
                    required={contractType === "fixed_term"}
                  />
                </label>
              )}
            </div>
            <div className="contract-impact-note">
              <CalendarClock size={18} />
              <span>
                Contractdata verandert geen bestaande toekenningen. Tijdelijke
                contracten worden als saldo-periode beschikbaar.
              </span>
            </div>
            <button className="button primary" type="submit" disabled={saving}>
              {editingContract ? <Pencil size={17} /> : <Plus size={17} />}
              {editingContract ? "Contract opslaan" : "Contract toevoegen"}
            </button>
          </form>
        )}

      {!canEdit && organization && (
        <p className="read-only-note surface settings-section">
          <BriefcaseBusiness size={17} /> Alleen een owner of organisatiebeheerder kan
          contractgegevens wijzigen.
        </p>
      )}
    </div>
  );
}
